import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Upload, X, Megaphone, ImageIcon, FileText, Trash2 } from 'lucide-react';
import {
  useAnnouncement,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useAddAnnouncementMedia,
  useDeleteAnnouncementMedia,
  useCountries,
  useDepartments,
} from '@/lib/queries';
import type { AnnouncementAttachment } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif';
const MAX_BYTES = 10 * 1024 * 1024;

export function AnnouncementFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { data: existing } = useAnnouncement(id);
  const { data: countries } = useCountries();
  const { data: departments } = useDepartments();
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const addMedia = useAddAnnouncementMedia();
  const deleteMedia = useDeleteAnnouncementMedia();
  const fileRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    title: '',
    body: '',
    pinned: false,
    audienceType: 'ALL' as string,
    audienceCountryIds: [] as string[],
    audienceDepartmentIds: [] as string[],
  });
  const [newFiles, setNewFiles] = useState<File[]>([]);
  // Adjuntos ya existentes (al editar), para poder eliminarlos.
  const [existingMedia, setExistingMedia] = useState<AnnouncementAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (existing && isEdit) {
      setForm({
        title: existing.title ?? '',
        body: existing.body ?? '',
        pinned: existing.pinned ?? false,
        audienceType: existing.audienceType ?? 'ALL',
        audienceCountryIds: existing.audienceCountryIds ?? [],
        audienceDepartmentIds: existing.audienceDepartmentIds ?? [],
      });
      setExistingMedia(existing.attachments ?? []);
    }
  }, [existing, isEdit]);

  const addPickedFiles = (files: FileList | null) => {
    if (!files) return;
    const picked: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_BYTES) {
        alert(`"${f.name}" supera 10 MB y no se agregó.`);
        continue;
      }
      picked.push(f);
    }
    setNewFiles((prev) => [...prev, ...picked]);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeExisting = async (att: AnnouncementAttachment) => {
    if (!id) return;
    try {
      await deleteMedia.mutateAsync({ id, s3Key: att.s3Key });
      setExistingMedia((prev) => prev.filter((m) => m.s3Key !== att.s3Key));
    } catch {
      alert('No se pudo eliminar el adjunto');
    }
  };

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const toggleArrayItem = (key: 'audienceCountryIds' | 'audienceDepartmentIds', val: string) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
    }));
  };

  const submit = async (status: 'DRAFT' | 'PUBLISHED') => {
    setError(null);
    setSaving(true);
    try {
      const payload = { ...form, status };
      let announcementId: string;

      if (isEdit) {
        await update.mutateAsync({ id: id!, payload });
        announcementId = id!;
      } else {
        const result = await create.mutateAsync(payload);
        announcementId = result.id;
      }

      // Subir cada archivo nuevo a la galería (uno por uno, presigned PUT).
      for (const file of newFiles) {
        const { uploadUrl } = await addMedia.mutateAsync({
          id: announcementId,
          payload: {
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            size: file.size,
          },
        });
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error(`No se pudo subir "${file.name}"`);
      }

      navigate(`/anuncios/${announcementId}`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err.message ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageTransition className="flex flex-col gap-6">
      <Link
        to="/anuncios"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary transition hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a anuncios
      </Link>

      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle text-primary">
          <Megaphone size={22} />
        </div>
        <h1 className="text-2xl font-bold text-font-primary">
          {isEdit ? 'Editar anuncio' : 'Nuevo anuncio'}
        </h1>
      </div>

      <FadeIn delay={0.05}>
        <Card className="p-6">
          <div className="flex flex-col gap-5">
          {/* Title */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Título <span className="text-error">*</span>
            </span>
            <input
              className="input h-10"
              maxLength={200}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Ej: Actualización de políticas de vacaciones 2026"
            />
            <span className="text-[10px] text-font-tertiary">
              {form.title.length}/200
            </span>
          </label>

          {/* Body */}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">
              Contenido <span className="text-error">*</span>
            </span>
            <textarea
              className="input min-h-[200px]"
              maxLength={5000}
              value={form.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Escribe el contenido del anuncio. Usa guiones (- ) para listas."
            />
            <span className="text-[10px] text-font-tertiary">
              {form.body.length}/5000
            </span>
          </label>

          {/* Audience */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-font-primary">Audiencia</span>
            <select
              className="input h-10 max-w-sm"
              value={form.audienceType}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  audienceType: v,
                  ...(v === 'ALL' && { audienceCountryIds: [], audienceDepartmentIds: [] }),
                  ...(v === 'BY_COUNTRY' && { audienceDepartmentIds: [] }),
                  ...(v === 'BY_DEPARTMENT' && { audienceCountryIds: [] }),
                }));
              }}
            >
              <option value="ALL">Todos los colaboradores</option>
              <option value="BY_COUNTRY">Por país</option>
              <option value="BY_DEPARTMENT">Por departamento</option>
              <option value="BY_COUNTRY_AND_DEPARTMENT">Por país y departamento</option>
            </select>

            {(form.audienceType === 'BY_COUNTRY' ||
              form.audienceType === 'BY_COUNTRY_AND_DEPARTMENT') && (
              <div className="mt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase text-font-tertiary">
                  Países
                </p>
                <div className="flex flex-wrap gap-2">
                  {countries?.items?.map((c: any) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleArrayItem('audienceCountryIds', c.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        form.audienceCountryIds.includes(c.id)
                          ? 'border-primary bg-primary-subtle text-primary'
                          : 'border-border bg-white text-font-secondary hover:border-primary'
                      }`}
                    >
                      {c.nameEs}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(form.audienceType === 'BY_DEPARTMENT' ||
              form.audienceType === 'BY_COUNTRY_AND_DEPARTMENT') && (
              <div className="mt-2">
                <p className="mb-1 text-[10px] font-semibold uppercase text-font-tertiary">
                  Departamentos
                </p>
                <div className="flex flex-wrap gap-2">
                  {departments?.items?.map((d: any) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleArrayItem('audienceDepartmentIds', d.id)}
                      className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                        form.audienceDepartmentIds.includes(d.id)
                          ? 'border-primary bg-primary-subtle text-primary'
                          : 'border-border bg-white text-font-secondary hover:border-primary'
                      }`}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Adjuntos (galería múltiple) */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold text-font-primary">
              Adjuntos — imágenes y documentos (opcional, máx 10 MB c/u)
            </span>

            {(existingMedia.length > 0 || newFiles.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {/* Adjuntos existentes */}
                {existingMedia.map((att) => (
                  <div
                    key={att.s3Key}
                    className="group relative h-20 w-20 overflow-hidden rounded-lg border border-border bg-bg"
                  >
                    {att.kind === 'image' ? (
                      <img src={att.url} alt={att.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center">
                        <FileText size={20} className="text-error" />
                        <span className="w-full truncate text-[9px] text-font-secondary">
                          {att.name}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeExisting(att)}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition group-hover:opacity-100"
                      title="Eliminar"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}

                {/* Archivos nuevos por subir */}
                {newFiles.map((f, i) => {
                  const isImg = f.type.startsWith('image/');
                  return (
                    <div
                      key={`${f.name}-${i}`}
                      className="group relative h-20 w-20 overflow-hidden rounded-lg border border-primary/30 bg-primary-subtle"
                    >
                      {isImg ? (
                        <img
                          src={URL.createObjectURL(f)}
                          alt={f.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center">
                          <FileText size={20} className="text-primary" />
                          <span className="w-full truncate text-[9px] text-font-secondary">
                            {f.name}
                          </span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setNewFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition group-hover:opacity-100"
                        title="Quitar"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              accept={ACCEPT}
              onChange={(e) => addPickedFiles(e.target.files)}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex h-10 w-fit items-center gap-2 rounded-md border border-dashed border-border bg-bg px-4 text-xs font-semibold text-font-secondary hover:border-primary hover:text-primary"
            >
              <Upload size={14} /> Agregar archivos
              <span className="inline-flex items-center gap-1 text-font-tertiary">
                <ImageIcon size={12} /> imágenes · <FileText size={12} /> docs
              </span>
            </button>
          </div>

          {/* Pinned */}
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(e) => set('pinned', e.target.checked)}
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-semibold text-font-primary">
              Fijar este anuncio arriba de la lista
            </span>
          </label>

          {error && (
            <div className="rounded-md border border-error/20 bg-error/10 px-3 py-2 text-sm text-error">
              {error}
            </div>
          )}
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-wrap justify-end gap-3 border-t border-border pt-5">
            <Button variant="outline" asChild>
              <Link to="/anuncios">Cancelar</Link>
            </Button>
            <Button
              variant="secondary"
              onClick={() => submit('DRAFT')}
              disabled={saving || !form.title.trim() || !form.body.trim()}
            >
              {saving ? 'Guardando...' : 'Guardar borrador'}
            </Button>
            <Button
              variant="gradient"
              onClick={() => submit('PUBLISHED')}
              disabled={saving || !form.title.trim() || !form.body.trim()}
            >
              {saving ? 'Publicando...' : 'Publicar'}
            </Button>
          </div>
        </Card>
      </FadeIn>
    </PageTransition>
  );
}
